import { useCallback, useEffect, useState } from 'react';
import { playerConfigDomain } from '../../../util/config';
import axios from 'axios';
import { ENTERPRISE_CONFIG_ID, isUsedOnMobile, SCENE_EDITOR_PLAYER_ACCOUNT_ID, SCENE_EDITOR_PLAYER_CONFIG_ID, setAdditionalStyleSheet } from '../../../util/util';
import i18n from '../../../i18n';
import { useDispatch } from 'react-redux';
import { setChatEmail, setChatUserId, setChatUsername } from '../../../actions/chatLoginActions';
import Cookies from 'js-cookie';

interface UseFetchPlayerConfigDataProps {
	playerConfigId: string;
	accountId: string;
	fromSceneEditor?: boolean;
	queryParams?: any;
	isMediaLive?: boolean;
	setLoading?: (_value: boolean) => void;
}

const useFetchPlayerConfigData = ({ playerConfigId, accountId, fromSceneEditor, queryParams, isMediaLive, setLoading }: UseFetchPlayerConfigDataProps) => {
	const dispatch = useDispatch();
	const [playerMobileConfig, setPlayerMobileConfig] = useState<any>({});
	const [playerDesktopConfig, setPlayerDesktopConfig] = useState<any>({});

	const [playerConfig, setPlayerConfig] = useState<any>();

	const isIgnoreChatCookies = useCallback((settings: any, ignoreChatCookies?: any) => {
		if (ignoreChatCookies) {
			return ignoreChatCookies === 'true';
		}
		return settings?.additionalSettings?.noChatCookies === true;
	}, []);

	const fetchPlayerConfigCallback = useCallback((desktopConfig?: any, mobileConfig?: any) => {
		if (fromSceneEditor) {
			return;
		}

		const settings = isUsedOnMobile(queryParams ?? {}) ? mobileConfig : desktopConfig;
		if (Object.keys(settings).length === 0) {
			return;
		}

		const language = queryParams.language ? queryParams.language : settings?.accessibilitySettings?.language;
		if (language) {
			i18n.changeLanguage(language);
			document.documentElement.lang = language || 'en';
		}

		if (isIgnoreChatCookies(settings, queryParams.ignoreChatCookies)) {
			Cookies.remove(`chat_ID_${accountId}`);
			Cookies.remove(`chat_name_${accountId}`);
			Cookies.remove(`chat_email_${accountId}`);
			dispatch(setChatUserId(''));
			dispatch(setChatUsername(''));
			dispatch(setChatEmail(''));
		}

		setAdditionalStyleSheet({
			settings,
			isMediaLive,
			paramSharing: queryParams.sharing,
			paramDownload: queryParams.downloadMedia,
		});
	}, [fromSceneEditor, queryParams, isIgnoreChatCookies, isMediaLive, accountId, dispatch]);

	const setPlayerConfigData = useCallback((configData: any) => {
		if (configData && configData.layouts) {
			const desktop = configData.layouts.find((el: any) => {
				return el.type === 'desktop';
			});
			desktop && setPlayerDesktopConfig({...desktop, id: fromSceneEditor ? SCENE_EDITOR_PLAYER_CONFIG_ID : playerConfigId || ENTERPRISE_CONFIG_ID });

			const mobile = configData.layouts.find((el: any) => {
				return el.type === 'mobile';
			});
			mobile && setPlayerMobileConfig({...mobile, id: fromSceneEditor ? SCENE_EDITOR_PLAYER_CONFIG_ID : playerConfigId || ENTERPRISE_CONFIG_ID });

			setPlayerConfig(configData);
			setLoading?.(false);
		}
	}, [fromSceneEditor, playerConfigId, setLoading]);

	useEffect(() => {
		let configUrl = accountId ? `${playerConfigDomain}${accountId}/settings/${playerConfigId || ENTERPRISE_CONFIG_ID}`: "";
		const enterpriseConfigUrl = `${playerConfigDomain}${accountId ?? SCENE_EDITOR_PLAYER_ACCOUNT_ID}/settings/${ENTERPRISE_CONFIG_ID}`;
		if (fromSceneEditor) {
			configUrl = `${playerConfigDomain}${SCENE_EDITOR_PLAYER_ACCOUNT_ID}/settings/${SCENE_EDITOR_PLAYER_CONFIG_ID}`;
		}

		if (!configUrl) {
			return;
		}

		axios.get(configUrl).then((response) => {
			if (response?.data) {
				setPlayerConfigData(response?.data);
			} else {
				axios.get(enterpriseConfigUrl).then((response) => {
					response?.data && setPlayerConfigData(response?.data);
				});
			}
		}).catch((_error) => {
			axios.get(enterpriseConfigUrl).then((response) => {
				response?.data && setPlayerConfigData(response?.data);
			});
		});
	}, [playerConfigId, accountId, fromSceneEditor, setPlayerConfigData]);

	useEffect(() => {
		fetchPlayerConfigCallback?.(playerDesktopConfig, playerMobileConfig);
	}, [playerMobileConfig, playerDesktopConfig, fetchPlayerConfigCallback]);

	return { playerConfig, playerMobileConfig, playerDesktopConfig };
};

export default useFetchPlayerConfigData;
